import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../../components/shared/useUser';
import { useTranslation } from 'react-i18next';
import { client, FileRequest, TrainingAreaMaterials } from '../../../app/services/api/orderManagementClient';

export const useTrainingMaterial = () => {
  //use hooks
  const { t } = useTranslation('trainingArea');
  const { selectedStore, user } = useContext(UserContext);

  //general state
  const [data, setData] = useState<TrainingAreaMaterials[]>([]);
  const [filteredData, setFilteredData] = useState<TrainingAreaMaterials[]>([]);
  const [loading, setLoading] = useState(false);
  const { hasPermissionTo } = useContext(UserContext);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const hasWritePermission = hasPermissionTo(['TrainingAreaWrite']);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  

  //snackbar modal state
  const [snackBarDuration, setSnackBarDuration] = useState<number | null>(6000);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackBarSeverity, setSnackBarSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('error');

  // modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
 
  useEffect(() => {
    setLoading(true);
    loadData();
  }, []);
  const clearAll = () => {
    loadData();
  };
  const loadData = () => {
    client.getTrainingMaterials( selectedStore?.storeNumber)
      .then((result: { data: TrainingAreaMaterials[]; }) => {
        setData(result.data);
        setFilteredData(result.data);
        setLoading(false);
     
      })
      .catch((error: unknown) => {
        setSnackBarProps('error', `${t('errorMessage')}: ${error}`);
      });
  };

  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);

    if (value.trim() === '') {
      setFilteredData(data); 
    } else {
      const lowercased = value.toLowerCase();
      const filtered = data.filter((item) =>
        item.FileName.toLowerCase().includes(lowercased),
      );
      setFilteredData(filtered);
    }
  };
  
  
 
  const deleteRow = async (materialId: string, fileName: string) => {
    if (!materialId || !fileName) return;
    setSnackBarProps('info', 'Deleting Training Material');
    setLoading(true);
    try {
      const result = await client.deleteTrainingMaterial(selectedStore?.storeNumber, materialId, fileName);
      if (result.success) {
        setSelectedItems(prev => prev.filter(selectedId => selectedId !== materialId));
        setSnackBarProps('success', 'Training material deleted successfully');
      } else {
        setSnackBarProps('error', `${t('errorMessage')}: ${result.message}`);
      }
    } catch (error) {
      setSnackBarProps('error', `${t('errorMessage')}: ${error}`);
    } finally {
      setLoading(false);
      loadData(); 
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    try {
      const fileNames = selectedItems.map(id => {
        const item = data?.find(item => item.Id.toString() === id);
        return item?.FileName || '';
      }).filter(name => name !== '');

      const response = await client.bulkDeleteMaterials(selectedStore?.storeNumber, fileNames);

      if (response.success) {
        setSnackBarProps('success', 'Training materials deleted successfully');
        setSelectedItems([]);
        loadData(); 
      } else {
        setSnackBarProps('error', `${t('errorMessage')}: ${response.message}`);
      }
    } catch (error) {
      setSnackBarProps('error', `${t('errorMessage')}: ${error}`);
    }
    
  };
  
  const handleToggleSelection = (id: string) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(itemId => itemId !== id) 
        : [...prev, id],
    );
  };

  /**
   * Maps raw backend error messages to user-friendly text.
   * Keeps internal details (request IDs, XML, etc.) out of the UI.
   */
  const getFriendlyErrorMessage = (raw: string | undefined): string => {
    if (!raw) return 'An unexpected error occurred. Please try again.';

    const lower = raw.toLowerCase();

    if (lower.includes('blobnotfound') || lower.includes('blob does not exist')) {
      return 'This file could not be found. It may have been deleted or moved.';
    }
    if (lower.includes('not found')) {
      return 'The requested file was not found. Please refresh and try again.';
    }
    if (lower.includes('unauthorized') || lower.includes('403') || lower.includes('401')) {
      return 'You do not have permission to access this file.';
    }
    if (lower.includes('timeout') || lower.includes('timed out')) {
      return 'The request timed out. Please check your connection and try again.';
    }

    return 'An error occurred while opening the file. Please try again later.';
  };

  const downloadFile = async (fileName: string, blobName: string) => {
    try {
      const blob = await client.downloadTrainingArea(selectedStore?.storeNumber, fileName);

      if (!blob || blob.size === 0) {
        setSnackBarProps('error', 'File not found or invalid response');
        return false;
      }

      if (blob.type === 'application/json' || blob.size < 1024) {
        const text = await blob.text();
        const json = JSON.parse(text);
        if (json.success === false) {
          const friendlyMessage = getFriendlyErrorMessage(json.message);
          setSnackBarProps('error', friendlyMessage);
          return false;
        }
      }

      const blobUrl = URL.createObjectURL(blob);

      const newTab = window.open(blobUrl, '_blank');

      if (!newTab) {
        URL.revokeObjectURL(blobUrl);
        setSnackBarProps('error', 'Unable to open file. Please allow pop-ups for this site.');
        return false;
      }

      setSnackBarProps('success', 'File opened successfully');
      return true;
    } catch (error) {
      setSnackBarProps('error', `${t('errorMessage')}: ${error}`);
      return false;
    }
  };

  const setSnackBarProps = (severity: 'error' | 'warning' | 'info' | 'success', message: string) => {
    severity === 'success' ? setSnackBarDuration(6000) : setSnackBarDuration(null);
    setIsSnackBarOpen(true);
    setSnackBarSeverity(severity);
    setSnackbarMessage(message);
  };
  
  const closeSnackBar = () => {
    setIsSnackBarOpen(false);
  };
  const handleClose = () => {
    setIsModalOpen(false);
  };
  const openModal = () => {
    setIsModalOpen(true);
  };

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const maxSize = 2 * 1024 * 1024; // 2MB

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...newFiles]);
      setPreviewOpen(true);
      e.target.value = '';
    }
  };

  const removeFileFromPreview = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveAllFiles = async () => {
    const allowedTypes = [
      'application/pdf', 
      'image/jpeg', 
      'image/png', 
    ];

    try {
      const invalidFiles: {name: string, reason: string}[] = [];

      // Check for duplicates within the selected files
      const fileNames = selectedFiles.map(file => file.name.toLowerCase());
      const duplicateNames = fileNames.filter((name, index) => fileNames.indexOf(name) !== index);
      
      if (duplicateNames.length > 0) {
        const uniqueDuplicates = Array.from(new Set(duplicateNames));
        setSnackBarProps(
          'error',
          `Duplicate files detected in selection: ${uniqueDuplicates.join(', ')}. Please remove duplicates before uploading.`,
        );
        return;
      }

      const allowedExtensions = ['.pdf', '.jpeg', '.jpg', '.png'];

      for (const file of selectedFiles) {
        const extension = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
        const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(extension);

        if (!isValidType) {
          invalidFiles.push({
            name: file.name,
            reason: 'Invalid file type. Supported types: PDF, JPG, JPEG, PNG',
          });
          continue;
        }
  
        if (file.size > maxSize) {
          invalidFiles.push({
            name: file.name,
            reason: 'File exceeds 2MB. Please compress it prior to upload',
          });
          continue;
        }
  
        // Check if file already exists in the system
        const isDuplicate = data?.some(item => 
          item.FileName.toLowerCase() === file.name.toLowerCase(),
        );
  
        if (isDuplicate) {
          invalidFiles.push({
            name: file.name,
            reason: 'A file with this name already exists',
          });
        }
      }

      if (invalidFiles.length > 0) {
        const details = invalidFiles.map(f => `${f.name}: ${f.reason}`).join('\n');
        setSnackBarProps(
          'error',
          details,
        );
        return;
      }

      const fileRequests: FileRequest[] = [];

      for (const file of selectedFiles) {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
  
        const fileData = await convertFileToBase64(file);
  
        fileRequests.push({
          fileData: fileData,
          fileName: file.name,
        });
      }

      await client.uploadTrainingArea(selectedStore?.storeNumber!, fileRequests);
      setSnackBarProps('success', 'Training material uploaded successfully');
      setSelectedFiles([]);
      setPreviewOpen(false);
      handleClose();
      loadData(); 
  
    } catch (error) {
      setSnackBarProps('error', `${t('errorMessage')}: ${error}`);
    }
  };
  console.log(selectedStore?.storeGroup);
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      };
      reader.onerror = error => reject(error);
    });
  };

  return {
    t,
    setIsModalOpen,
    isModalOpen,
    closeSnackBar,
    handleClose,
    openModal,
    isSnackBarOpen,
    snackBarSeverity,
    snackbarMessage,
    snackBarDuration,
    loading,
    data,
    deleteRow,
    downloadFile,
    handleToggleSelection,
    handleBulkDelete,
    selectedStore,
    hasWritePermission,
    selectedItems,
    setSelectedItems,
    filteredData,  
    searchQuery,   
    handleSearchChange,
    handleSaveAllFiles,
    handleFileSelection,  
    removeFileFromPreview,
    setSelectedFiles,
    setPreviewOpen,
    previewOpen,
    selectedFiles,
    clearAll,
  };
};



